<template>
  <div>
    <div class="content">
      <div v-show="!textPreview" id="email-builder" class="builder"></div>
      <email-text-preview
        v-show="textPreview"
        ref="textPreview"
        :on-close="closeTextPreview"
      />
    </div>
    <div class="overlay"></div>
    <div class="control-bar">
      <div class="control-buttons">
        <div
          v-if="
            $can('updateProjectItem') || $can('CampaignTemplate:FullAccess')
          "
          class="control-button"
          @click="save"
        >
          <i class="icon-check" />
          Save
        </div>
        <div class="control-button" @click="preview">
          <i class="icon-eye" />
          Preview
        </div>
        <div class="control-button" @click="openTextPreview">
          <i class="icon-doc-text" />
          Text Preview
        </div>
        <div class="control-button" @click="sendTest">
          <i class="icon-doc-text" />
          Test Send
        </div>
        <div class="control-button" @click="confirmClose">
          <i class="icon-cancel" />
          Discard
        </div>
      </div>
      <div class="launcher">
        <h1 class="launcher-button">
          <el-button
            icon="el-icon-question"
            type="primary"
            @click="showZendeskWidget"
            round
          >
            Help
          </el-button>
        </h1>
      </div>
    </div>
    <vuedal></vuedal>
  </div>
</template>

<script>
import Bee from 'bee-plugin'
import BeeAuth from '@/common/components/email/BeeAuth'
import BlankTemplate from '@/common/components/email/BlankTemplate'
import EmailTextPreview from '@/common/components/email/EmailTextPreview'
import EditSingleRowMode from '@/common/components/email/EditSingleRowMode'
import { Component as Vuedal } from 'vuedals'
import RestApi from '@/core/services/RestApi'
import { MessageBox } from 'element-ui'
import { projectEndpoint } from '@/core/helpers'
import { $hasFeature } from '@/core/utils/VueAcl'
import { BeeLanguageEnum } from '@/common/enum/BeeLanguageEnum'
import { emailSavedRowTypeEnum } from '@/modules/email-saved-rows/enum/EmailSavedRowTypeEnum'

export default {
  name: 'EmailBuilder',
  components: {
    EmailTextPreview,
    Vuedal,
    // eslint-disable-next-line vue/no-unused-components
    EditSingleRowMode
  },
  props: {
    template: [Object, String],
    onSave: Function,
    onError: Function,
    onClose: Function,
    onConfirmClose: Function,
    onSendTest: Function,
    generateTextPreview: Function,
    flags: Object
  },

  data: () => {
    return {
      textPreview: false,
      sendingTest: false,
      BeeLanguageEnum,
      pendingRowUpdate: null
    }
  },

  created() {
    if (
      'xp' in window &&
      window.xp.project.id &&
      typeof localStorage !== 'undefined'
    ) {
      localStorage.setItem('xp-project-id', window.xp.project.id)
      localStorage.setItem('xp-access-token', window.xp.accessToken)
    }
    this.loadBuilder()
  },

  beforeDestroy() {
    const el = document.getElementById('email-builder')
    if (el) {
      el.parentElement.removeChild(el)
    }
  },

  computed: {
    beeLanguage() {
      let store = this.$store || window.vue.$store
      if (!store) {
        return BeeLanguageEnum['en']
      }

      let language = BeeLanguageEnum[store.state.app.user.language]
      if (!language) {
        language = BeeLanguageEnum[store.state.app.provider.language]
      }
      return language || BeeLanguageEnum['en']
    }
  },

  methods: {
    async loadBuilder() {
      const specialLinks = [
        {
          type: 'links',
          label: 'Unsubscribe',
          link: '{{ system.url.unsubscribe }}'
        }
      ]
      if (this.flags && this.flags.consentPreference) {
        specialLinks.push({
          type: 'links',
          label: 'Unsubscribe from all',

          link: '{{ system.url.unsubscribeall }}'
        })
      }
      specialLinks.push({
        type: 'links',
        label: 'View Online',
        link: '{{ system.url.archive }}'
      })

      let projectId = window.xp.project.id

      let shopboxPluginAllowed = false
      let voucherCodesAllowed = false

      try {
        const result = await RestApi.get(`projects/${projectId}/config`)

        if (result.data) {
          shopboxPluginAllowed =
            (result.data.features && result.data.features.shopboxEnabled) ||
            false
          voucherCodesAllowed =
            (result.data.features &&
              result.data.features.allowVoucherCodes &&
              this.flags &&
              this.flags.voucherCodesAllowed) ||
            false
        }
      } catch (_) {
        shopboxPluginAllowed = false
      }

      let attributes = await RestApi(projectEndpoint({ url: 'attributes' }))
      const mergeTags = attributes.data.map(record => {
        let path
        if (
          !$hasFeature('twig') ||
          record.name.match('^[a-zA-Z]+[a-zA-Z0-9_.]{0,}$')
        ) {
          path = record.name
        } else {
          path = "user.attributes['" + record.name.replaceAll(`'`, `\\'`) + "']"
        }

        return {
          name: record.name,
          value: `{{ ${path} }}`
        }
      })

      let fonts = await RestApi(
        projectEndpoint({ method: 'get', url: 'web-font' })
      )
      let customFonts = fonts.data.map(record => ({
        name: record.name,
        fontFamily: record.family,
        url: record.url
      }))

      const that = this

      const canEditSyncedRow = this.$can('EmailSavedRows::Update')

      const externalContentURLs = [
        {
          name: this.$t('EmailBuilder_Saved'),
          value: new URL(
            '/builder/saved-rows?project_id=' + projectId,
            window.location.href
          ).href
        },
        {
          name: this.$t('EmailBuilder_Synced'),
          value: new URL(
            '/builder/saved-rows?synced=1&project_id=' + projectId,
            window.location.href
          ).href
        }
      ]

      let config = {
        container: 'email-builder',
        language: this.beeLanguage,
        roleHash: 'PowerUser',
        trackChanges: true,
        saveRows: true,
        advancedPermissions: {
          rows: {
            toolbar: {
              editSyncedRow: {
                show: true,
                locked: !canEditSyncedRow
              }
            }
          }
        },
        editorFonts: {
          showDefaultFonts: true,
          customFonts: customFonts
        },
        specialLinks,
        mergeTags,
        addOns: [
          {
            id: 'product-recommendations-dialog',
            enabled: shopboxPluginAllowed
          },
          {
            id: 'ai-integration',
            enabled: $hasFeature('aiTextGeneration')
          },
          {
            id: 'voucher-codes-addon',
            enabled: voucherCodesAllowed
          }
        ],
        rowsConfiguration: {
          emptyRows: true,
          defaultRows: false,
          externalContentURLs
        },

        onSave: (jsonFile, htmlFile) => {
          if (this.pendingRowUpdate) {
            this.finishPendingRowUpdate(jsonFile)
            return
          }

          if (this.textPreview) {
            this.updateTextPreview(htmlFile)
          } else if (this.sendingTest && this.onSendTest) {
            this.sendingTest = false
            return this.onSendTest(htmlFile)
          } else if (this.onSave) {
            this.onSave(jsonFile, htmlFile)
          }
          this.sendingTest = false
        },
        onError: errorMessage => {
          if (this.onError) {
            this.onError(errorMessage)
          }
        },
        onChange: () => {
          this.isChanged = true
        },

        onSaveRow: function(rowJSON, rowHTML, pageJSON) {
          const parsedRowJSON = JSON.parse(rowJSON)
          const metadata = parsedRowJSON.metadata || {}
          const isSynced = Boolean(parsedRowJSON.synced || metadata.synced)

          const payload = {
            name: metadata.name || '',
            type: isSynced
              ? emailSavedRowTypeEnum.SYNCED_ROW
              : emailSavedRowTypeEnum.SAVED_ROW,
            content: rowHTML,
            meta: JSON.stringify({ rowJSON: parsedRowJSON, pageJSON })
          }

          RestApi.post('projects/' + projectId + '/content', payload)
        },
        contentDialog: {
          mergeTags: {
            label: 'Dynamic content',
            handler: function(resolve, reject) {
              that.createBeePluginWindow(
                resolve,
                reject,
                {},
                'bee-dynamic-content-plugin'
              )
            }
          },
          saveRow: {
            handler: async function(resolve, reject) {
              try {
                const syncedCheckboxHtml = `
                    <label style="margin-top: 15px; display: block; cursor: pointer; font-size: 14px; user-select: none;">
                      <input
                        type="checkbox"
                        id="synced-checkbox"
                        style="margin-right: 8px; cursor: pointer; vertical-align: middle;"
                      />
                      <span style="vertical-align: middle;">${that.$t(
                        'EmailBuilder_SaveRow_SyncedLabel'
                      )}</span>
                    </label>
                  `
                const htmlContent = `
                  <div style="text-align: left;">
                    <input
                      type="text"
                      id="row-name-input"
                      placeholder="${that.$t(
                        'EmailBuilder_SaveRow_NamePlaceholder'
                      )}"
                      style="width: 100%; padding: 10px; border: 1px solid #dcdfe6; border-radius: 4px; font-size: 14px; box-sizing: border-box;"
                    />
                    ${syncedCheckboxHtml}
                  </div>
                `

                // Pre-fetch GUID before opening the dialog, as required for synced rows
                let generatedGuid = null
                try {
                  const guidResponse = await RestApi.get(
                    `projects/${projectId}/generate/guid`
                  )
                  generatedGuid = guidResponse.data && guidResponse.data.guid
                } catch (_) {
                  // GUID fetch failed; synced rows will not be available
                }

                await MessageBox({
                  title: that.$t('EmailBuilder_SaveRow_Title'),
                  message: htmlContent,
                  dangerouslyUseHTMLString: true,
                  showCancelButton: true,
                  confirmButtonText: that.$t('Button_Save'),
                  cancelButtonText: that.$t('Button_Cancel'),
                  beforeClose: (action, instance, done) => {
                    if (action === 'confirm') {
                      const nameInput = document.getElementById(
                        'row-name-input'
                      )
                      const syncedCheckbox = document.getElementById(
                        'synced-checkbox'
                      )
                      const name = nameInput ? nameInput.value.trim() : ''
                      const synced = syncedCheckbox
                        ? syncedCheckbox.checked
                        : false

                      if (!name) {
                        that.$message.error(
                          that.$t('EmailBuilder_SaveRow_NameRequired')
                        )
                        return
                      }

                      if (synced && !generatedGuid) {
                        that.$message.error(
                          that.$t('EmailBuilder_SaveRow_GuidError')
                        )
                        return
                      }

                      instance.confirmButtonLoading = false
                      done()

                      const metadata = { name }
                      if (synced) {
                        metadata.guid = generatedGuid
                      }

                      resolve(metadata, { synced })
                    } else {
                      done()
                      reject()
                    }
                  }
                })
              } catch (error) {
                reject()
              }
            }
          },
          addOn: {
            handler: function(resolve, reject, args) {
              const { contentDialogId, value } = args

              if (contentDialogId === 'product-recommendations-dialog') {
                that.createBeePluginWindow(
                  resolve,
                  reject,
                  value,
                  'bee-shopbox-plugin'
                )
              } else if (contentDialogId === 'voucher-codes-addon') {
                that.createBeePluginWindow(
                  resolve,
                  reject,
                  value,
                  'bee-voucher-plugin'
                )
              } else {
                reject('Unknown plugin')
              }
            }
          },
          editSyncedRow: {
            label: that.$t('EmailBuilder_EditSyncedRow_Title'),
            description: that.$t('EmailBuilder_EditSyncedRow_Description'),
            notPermittedDescription: that.$t(
              'EmailBuilder_EditSyncedRow_NotPermittedDescription'
            ),
            handler: async function(resolve, reject, args) {
              try {
                // args IS the row itself, not nested under args.row
                const rowGuid = args.metadata && args.metadata.guid

                if (!rowGuid) {
                  that.$message.error(
                    that.$t('EmailBuilder_EditSyncedRow_GuidMissing')
                  )
                  reject()
                  return
                }

                // Filter by GUID server-side; the list endpoint is paginated
                const savedRows = await RestApi.get(
                  `projects/${projectId}/email-saved-rows`,
                  {
                    params: { synced: 1, guid: rowGuid }
                  }
                )

                const syncedRow = savedRows.data[0]

                if (!syncedRow) {
                  that.$message.warning(
                    that.$t('EmailBuilder_EditSyncedRow_NotFound')
                  )
                  reject()
                  return
                }

                // Get usage counts
                const countsResponse = await RestApi.get(
                  `projects/${projectId}/content/${syncedRow.id}/usages`
                )

                const {
                  campaign_count: campaignCount = 0,
                  campaign_message_count: messageCount = 0,
                  message_template_count: messageTemplateCount = 0,
                  campaign_template_count: campaignTemplateCount = 0
                } = countsResponse.data || {}

                // Show dialog with options
                const h = that.$createElement
                const message = h('div', [
                  h(
                    'p',
                    { style: { marginBottom: '10px' } },
                    that.$t('EmailBuilder_EditSyncedRow_UsageHeading')
                  ),
                  h('ul', { style: { marginBottom: '15px' } }, [
                    h(
                      'li',
                      that.$t('EmailBuilder_EditSyncedRow_UsageCampaigns', {
                        count: campaignCount,
                        label:
                          campaignCount === 1
                            ? that.$t('EmailBuilder_EditSyncedRow_CampaignOne')
                            : that.$t('EmailBuilder_EditSyncedRow_CampaignMany')
                      })
                    ),
                    h(
                      'li',
                      that.$t('EmailBuilder_EditSyncedRow_UsageMessages', {
                        count: messageCount,
                        label:
                          messageCount === 1
                            ? that.$t('EmailBuilder_EditSyncedRow_MessageOne')
                            : that.$t('EmailBuilder_EditSyncedRow_MessageMany')
                      })
                    ),
                    h(
                      'li',
                      that.$t(
                        'EmailBuilder_EditSyncedRow_UsageMessageTemplates',
                        {
                          count: messageTemplateCount,
                          label:
                            messageTemplateCount === 1
                              ? that.$t(
                                  'EmailBuilder_EditSyncedRow_MessageTemplateOne'
                                )
                              : that.$t(
                                  'EmailBuilder_EditSyncedRow_MessageTemplateMany'
                                )
                        }
                      )
                    ),
                    h(
                      'li',
                      that.$t(
                        'EmailBuilder_EditSyncedRow_UsageCampaignTemplates',
                        {
                          count: campaignTemplateCount,
                          label:
                            campaignTemplateCount === 1
                              ? that.$t(
                                  'EmailBuilder_EditSyncedRow_CampaignTemplateOne'
                                )
                              : that.$t(
                                  'EmailBuilder_EditSyncedRow_CampaignTemplateMany'
                                )
                        }
                      )
                    )
                  ]),
                  h(
                    'p',
                    { style: { marginBottom: '10px' } },
                    that.$t('EmailBuilder_EditSyncedRow_Question')
                  ),
                  h('ul', { style: { marginBottom: '10px' } }, [
                    h('li', [
                      h(
                        'strong',
                        that.$t('EmailBuilder_EditSyncedRow_OptionUnsyncLabel')
                      ),
                      ' ',
                      that.$t(
                        'EmailBuilder_EditSyncedRow_OptionUnsyncDescription'
                      )
                    ]),
                    h('li', [
                      h(
                        'strong',
                        that.$t('EmailBuilder_EditSyncedRow_OptionEditAllLabel')
                      ),
                      ' ',
                      that.$t(
                        'EmailBuilder_EditSyncedRow_OptionEditAllDescription'
                      )
                    ])
                  ])
                ])

                await MessageBox({
                  title: that.$t('EmailBuilder_EditSyncedRow_Title'),
                  message: message,
                  showCancelButton: true,
                  distinguishCancelAndClose: true,
                  confirmButtonText: that.$t(
                    'EmailBuilder_EditSyncedRow_ConfirmButton'
                  ),
                  cancelButtonText: that.$t(
                    'EmailBuilder_EditSyncedRow_CancelButton'
                  ),
                  closeOnClickModal: false,
                  beforeClose: async (action, instance, done) => {
                    if (action === 'confirm') {
                      // Edit everywhere - open Edit Single Row Mode
                      // Close this dialog first, then open the editor
                      done()
                      setTimeout(() => {
                        that
                          .openEditSingleRowMode(syncedRow, args)
                          .then(async updatedRow => {
                            // Row was saved successfully
                            // Now update it in the current template
                            try {
                              await that.updateRowInCurrentTemplate(
                                updatedRow,
                                args
                              )
                              that.$message.success(
                                that.$t(
                                  'EmailBuilder_EditSyncedRow_UpdateSuccess'
                                )
                              )
                            } catch (error) {
                              that.$message.warning(
                                that.$t(
                                  'EmailBuilder_EditSyncedRow_UpdateWarning'
                                )
                              )
                            }

                            resolve(true) // Keep synced
                          })
                          .catch(error => {
                            if (
                              error &&
                              error.message &&
                              error.message === 'User cancelled'
                            ) {
                              reject()
                              return
                            }

                            that.$message.error(
                              that.$t('EmailBuilder_EditSyncedRow_OpenError')
                            )
                            reject()
                          })
                      }, 100)
                    } else if (action === 'cancel') {
                      // Unsync and edit here
                      // https://docs.beefree.io/beefree-sdk/rows/reusable-content/sync/implement-synced-rows#editing-synced-rows
                      // Do NOT toggle instance.confirmButtonLoading here —
                      // Element UI's MessageBox is a singleton and the flag
                      // leaks into the next $confirm (e.g. beforeRouteLeave).
                      try {
                        that.$message.success(
                          that.$t('EmailBuilder_EditSyncedRow_UnsyncSuccess')
                        )
                        done()
                        resolve(false)
                      } catch (error) {
                        that.$message.error(
                          that.$t('EmailBuilder_EditSyncedRow_UnsyncError')
                        )
                        reject()
                      }
                    } else {
                      done()
                      reject()
                    }
                  }
                })
              } catch (error) {
                console.error('editSyncedRow error:', error)
                reject()
              }
            }
          }
        }
      }

      this.textPreview = false

      let template = this.template
      if (!template) {
        template = BlankTemplate
      }

      BeeAuth.getToken().then(token => {
        config.uid = token.uid
        config.roleHash = token.roleHash
        this.bee = new Bee(token)
        this.bee.start(config, template)
      })
    },

    save() {
      this.textPreview = false
      this.bee.save()
    },

    preview() {
      this.textPreview = false
      this.bee.preview()
    },

    openTextPreview() {
      this.$refs.textPreview.text = ''
      this.$refs.textPreview.loading = true
      this.textPreview = true
      this.bee.save()
    },

    updateTextPreview(html) {
      this.generateTextPreview(html, text => {
        this.$refs.textPreview.text = text
        this.$refs.textPreview.loading = false
      })
    },

    closeTextPreview() {
      this.textPreview = false
    },

    sendTest() {
      this.sendingTest = true
      this.bee.save()
    },

    confirmClose() {
      if (this.isChanged) {
        this.onConfirmClose()
      } else {
        this.close()
      }
    },

    close() {
      if (this.onClose) {
        this.onClose()
      }
    },

    showZendeskWidget() {
      this.$zendesk.show()
      this.$zendesk.open()
    },

    createBeePluginWindow(resolve, reject, value, componentName) {
      this.$vuedals.open({
        name: componentName + '-modal',

        props: {
          resolve: resolve,
          reject: reject,
          'current-state': this.decodeValue(value)
        },

        component: componentName,

        dismissable: false,

        closeOnBackdrop: false
      })
    },

    finishPendingRowUpdate(jsonFile) {
      const pendingRowUpdate = this.pendingRowUpdate
      this.pendingRowUpdate = null

      if (!pendingRowUpdate) {
        return
      }

      const {
        updatedRow,
        currentRow,
        resolve,
        reject,
        timeoutId
      } = pendingRowUpdate

      if (timeoutId) {
        clearTimeout(timeoutId)
      }

      try {
        let templateData = jsonFile
        if (typeof jsonFile === 'string') {
          templateData = JSON.parse(jsonFile)
        }

        const rows = templateData.page && templateData.page.rows
        const rowUuid = currentRow.metadata && currentRow.metadata.guid

        if (!rows) {
          reject(new Error('Invalid template structure'))
          return
        }

        if (!rowUuid) {
          reject(new Error('Row UUID not found'))
          return
        }

        let replacedCount = 0
        for (let i = 0; i < rows.length; i++) {
          if (rows[i].metadata && rows[i].metadata.guid === rowUuid) {
            rows[i] = JSON.parse(JSON.stringify(updatedRow))
            replacedCount++
          }
        }

        if (replacedCount === 0) {
          reject(new Error('Row not found in template'))
          return
        }

        setTimeout(() => {
          this.bee.load(templateData)
          this.$message.success(
            this.$t('EmailBuilder_EditSyncedRow_TemplateUpdated')
          )
          resolve()
        }, 100)
      } catch (error) {
        reject(error)
      }
    },

    openEditSingleRowMode(savedRow, rowData) {
      const that = this
      return new Promise((resolve, reject) => {
        that.$vuedals.open({
          name: 'edit-single-row-mode',
          size: 'xl',
          dismissable: false,
          closeOnBackdrop: false,

          props: {
            savedRow: savedRow,
            rowData: rowData,
            onSave: updatedRow => {
              that.$vuedals.close()
              // Small delay to ensure modal is closed before resolving
              setTimeout(() => {
                resolve(updatedRow)
              }, 50)
            },
            onCancel: () => {
              that.$vuedals.close()
              reject(new Error('User cancelled'))
            }
          },

          component: EditSingleRowMode
        })
      })
    },

    decodeValue(value) {
      if (!value.hasOwnProperty('html')) {
        return {}
      }

      const data = value.html
      try {
        const parser = new DOMParser()
        this.parsedData = JSON.parse(
          decodeURIComponent(
            parser
              .parseFromString(data, 'text/html')
              .documentElement.querySelector('div').dataset.pluginState
          )
        )
        return this.parsedData
      } catch (e) {
        // parsing exception
        return {}
      }
    },

    /**
     * Update a synced row in the current template
     * @param {Object} updatedRow - The updated row JSON
     * @param {Object} currentRow - The current row in the template
     */
    async updateRowInCurrentTemplate(updatedRow, currentRow) {
      if (!this.bee) {
        throw new Error('Bee instance not available')
      }

      return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          if (
            this.pendingRowUpdate &&
            this.pendingRowUpdate.timeoutId === timeoutId
          ) {
            this.pendingRowUpdate = null
          }
          reject(new Error('Timed out while updating the template row'))
        }, 5000)

        // Set pending update - will be handled in onSave
        this.pendingRowUpdate = {
          updatedRow,
          currentRow,
          resolve,
          reject,
          timeoutId
        }

        // Trigger save to get current template JSON
        this.bee.save()
      })
    }
  }
}
</script>

<style scoped>
.overlay {
  position: fixed;
  z-index: 1000000;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background: #32373a;
}
.content {
  position: fixed;
  z-index: 1000001;
  top: 55px;
  left: 0;
  right: 0;
  bottom: 0;
}
.builder {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}
.control-bar {
  position: fixed;
  z-index: 1000002;
  top: 0;
  left: 0;
  right: 0;
  height: 55px;
  background: #505659;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.control-buttons {
  float: left;
}
.control-button {
  cursor: pointer;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  border-radius: 4px;
  -webkit-border-radius: 4px;
  -moz-border-radius: 4px;
  float: left;
  height: 55px;
  line-height: 55px;
  font-size: 14px;
  color: #ffffff;
  padding: 0 5px;
  margin-right: 20px;
}
.control-button:hover {
  opacity: 0.8;
}
.launcher {
  display: flex;
  align-items: center;
}
h1.launcher-button {
  display: inline-block;
}

>>> .vuedals {
  z-index: 10000000;
}

>>> .vuedal {
  max-height: 85vh;
  overflow: hidden;
}

>>> .vuedal.edit-single-row-mode {
  max-height: 95vh;
  max-width: 95vw;
  width: 95vw;
}
</style>



// WEBPACK FOOTER //
// src/common/components/email/EmailBuilder.vue